import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Container,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  OutlinedInput,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Delete, CloudUpload, Add, Download, Save, CallSplit } from "@mui/icons-material";
import { apiUrl } from "../apiConfig.js";
import { Tooltip } from "@mui/material";
import {
  CAPITALIZACION_SALIDA,
  CAPITALIZACION_ENTRADA,
  ensureDefaultConceptos,
} from "../data/conceptosCapitalizacion";

// Celda de importe: mientras está enfocada muestra el valor crudo editable
// ($1234.5) y al perder el foco lo muestra formateado ($1,234.50). El estado
// de foco es local en lugar de leerse de document.activeElement durante el
// render (lo cual era impuro y frágil).
function ImporteCell({ record, updateRecord, formatCurrency, fieldProps }) {
  const [focused, setFocused] = useState(false);

  const displayValue = focused
    ? record.importe ?? ""
    : record.importe === "" || record.importe == null
    ? ""
    : `$${formatCurrency(record.importe)}`;

  return (
    <TextField
      type="text"
      value={displayValue}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.]/g, "");
        if ((raw.match(/\./g) || []).length > 1) return;
        updateRecord(record.id, "importe", raw === "" ? "" : raw);
      }}
      onBlur={(e) => {
        setFocused(false);
        const raw = e.target.value.replace(/[^0-9.]/g, "");
        const finalValue = raw === "" ? "" : Number(raw);
        updateRecord(record.id, "importe", finalValue);
      }}
      placeholder="$0.00"
      {...fieldProps}
    />
  );
}

function EntradasSalidas() {
  const now = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });

  const [loadingExcel, setLoadingExcel] = useState(false);

  const handleExportExcel = async () => {
    try {
      setLoadingExcel(true);
      const token = sessionStorage.getItem("token");
      const { year, month } = selectedPeriod;

      const response = await fetch(
        apiUrl(`api/v1/balanza/resumen/excel?year=${year}&month=${month}`),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al generar el Excel");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const formattedMonth = String(month).padStart(2, "0");
      a.download = `${year}-${formattedMonth}-01.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel error:", err);
      setError("No se pudo descargar el archivo Excel.");
    } finally {
      setLoadingExcel(false);
    }
  };

  const MONTHS = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const navigate = useNavigate();

  const uid = () =>
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [error, setError] = useState("");

  const [openAddAccount, setOpenAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({
    tipoCuenta: "",
    apodo: "",
    titular: "",
    banco: "",
    numeroCuenta: "",
    saldoBase: "",
  });

  const [openUploadCSV, setOpenUploadCSV] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [catalogoConceptos, setCatalogoConceptos] = useState([]);

  const [openDesglose, setOpenDesglose] = useState(false);
  const [desgloseRecord, setDesgloseRecord] = useState(null);
  const [desgloseLines, setDesgloseLines] = useState([
    { importe: "", descripcion: "", conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "" },
  ]);

  const [openDeleteAll, setOpenDeleteAll] = useState(false);

  const [resumenCuentas, setResumenCuentas] = useState([]);

  const selectedPeriodRef = useRef(selectedPeriod);
  useEffect(() => {
    selectedPeriodRef.current = selectedPeriod;
  }, [selectedPeriod]);

  const recordsRef = useRef(records);
  useEffect(() => {
    recordsRef.current = records;
  }, [records]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchRecordsForPeriod = useCallback(async (year, month) => {
    setLoadingRecords(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        apiUrl(`api/v1/balanza/resumen?year=${year}&month=${month}`),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        const movimientos = data.movimientos || [];
        setResumenCuentas(data.cuentas || []);

        setRecords(
          movimientos.map((r) => ({
            ...r,
            id: uid(),
            importe: toNumber(r.importe),
            isEditable: true,
            cuenta: r.cuenta || r.apodo || "",
          }))
        );
      } else {
        console.error("Error al cargar registros");
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    fetchRecordsForPeriod(selectedPeriod.year, selectedPeriod.month);
  }, [selectedPeriod.year, selectedPeriod.month, fetchRecordsForPeriod]);

  const persistRecords = useCallback(
    async (recordsToSave, year, month) => {
      setError("");
      const token = sessionStorage.getItem("token");
      setLoading(true);

      try {
        const response = await fetch(
          apiUrl(`api/v1/balanza/update?year=${year}&month=${month}`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(
              (recordsToSave || []).map((record) => ({
                ...record,
                apodo: record.cuenta || record.apodo || "",
              }))
            ),
          }
        );

        if (response.ok) {
        } else if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("token");
          setError("Sesión expirada. Por favor inicia sesión nuevamente.");
          navigate("/login");
        } else {
          const errData = await response.json();
          throw new Error(errData.message || "Error del servidor");
        }
      } catch (err) {
        console.error("Save error:", err);
        setError(
          err.message === "Failed to fetch"
            ? "Error de conexión: El servidor no está disponible"
            : err.message
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  const handlePeriodChange = async (field, value) => {
    try {
      const { year, month } = selectedPeriodRef.current;
      await persistRecords(recordsRef.current, year, month);
      const newPeriod = { ...selectedPeriodRef.current, [field]: value };
      setSelectedPeriod(newPeriod);
    } catch (err) {
      console.error("Error cambiando de periodo:", err);
    }
  };

  useEffect(() => {
    const fetchCatalogoConceptos = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const res = await fetch(apiUrl("api/v1/catalogo/conceptos"), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          // Los conceptos de "Capitalizaciones a cuentas propias" siempre están
          // disponibles para poder categorizar el desglose.
          const conceptos = ensureDefaultConceptos(data.conceptos || []);
          setCatalogoConceptos(conceptos);
          setRecords((prev) =>
            prev.map((r) => {
              if (!r.conceptoCatalogo) return r;
              const exists = conceptos.some(
                (c) => c.conceptoCatalogo === r.conceptoCatalogo
              );
              if (!exists) {
                return { ...r, conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "" };
              }
              return r;
            })
          );
        } else {
          console.error("Error al cargar conceptos de catálogo");
        }
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchCatalogoConceptos();
  }, []);

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => {
        setStatus("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const fetchAccounts = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(apiUrl("api/v1/balanza/cuentas"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(Array.isArray(data) ? data : []);
      } else {
        console.error("Error al cargar cuentas");
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const sortedAccounts = useMemo(() => {
    const efectivos = accounts.filter((a) => a.tipoCuenta === "Efectivo");
    const debitos = accounts.filter((a) => a.tipoCuenta === "Debito");
    const creditos = accounts.filter((a) => a.tipoCuenta === "Credito");
    return [...efectivos, ...debitos, ...creditos];
  }, [accounts]);

  // Agrega entradas/salidas por cuenta en un solo recorrido de `records`.
  // Antes esto se calculaba con records.filter() por cada cuenta y por cada
  // fila de la tabla de resumen (O(5 * cuentas * movimientos)) en cada render.
  const aggByAccount = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const key = r.cuenta;
      let agg = map.get(key);
      if (!agg) {
        agg = { entradasBrutas: 0, salidasBrutas: 0 };
        map.set(key, agg);
      }
      const imp = toNumber(r.importe);
      if (r.concepto === "Entrada") agg.entradasBrutas += imp;
      else if (r.concepto === "Salida") agg.salidasBrutas += imp;
    }
    return map;
  }, [records]);

  const generalTotals = useMemo(() => {
    const entradasTotal = records
      .filter((r) => r.concepto === "Entrada")
      .reduce((sum, r) => sum + Math.abs(toNumber(r.importe)), 0);

    const salidasTotal = records
      .filter((r) => r.concepto === "Salida")
      .reduce((sum, r) => sum + Math.abs(toNumber(r.importe)), 0);

    const diferenciaTotal = entradasTotal - salidasTotal;

    const resumenElegibles = (resumenCuentas || []).filter(
      (c) => c.tipoCuenta === "Efectivo" || c.tipoCuenta === "Debito"
    );

    const saldoInicialTotal = resumenElegibles.reduce(
      (sum, c) => sum + toNumber(c.saldoDisponible),
      0
    );

    const saldoDisponibleTotal = resumenElegibles.reduce((sum, c) => {
      const agg = aggByAccount.get(c.apodo);
      const entradasBrutas = agg ? agg.entradasBrutas : 0;
      const salidasBrutas = agg ? agg.salidasBrutas : 0;
      const diferencia = entradasBrutas - salidasBrutas;
      return sum + (toNumber(c.saldoDisponible) + diferencia);
    }, 0);

    return {
      entradasTotal,
      salidasTotal,
      diferenciaTotal,
      saldoInicialTotal,
      saldoDisponibleTotal,
    };
  }, [records, resumenCuentas, aggByAccount]);

  const firstCreditIndex = useMemo(
    () => sortedAccounts.findIndex((a) => a.tipoCuenta === "Credito"),
    [sortedAccounts]
  );

  const accountColumnSx = (i) =>
    firstCreditIndex !== -1 && i === firstCreditIndex
      ? { borderLeft: "2px solid", borderLeftColor: "divider" }
      : {};

  const FIRST_COL_WIDTH = 160;
  const stickyFirstColHeaderSx = {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: "background.paper",
    minWidth: FIRST_COL_WIDTH,
    width: FIRST_COL_WIDTH,
    borderRight: "1px solid",
    borderRightColor: "divider",
  };
  const stickyFirstColCellSx = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: "background.paper",
    minWidth: FIRST_COL_WIDTH,
    width: FIRST_COL_WIDTH,
    borderRight: "1px solid",
    borderRightColor: "divider",
  };

  const generalHeaderSx = {
    backgroundColor: "action.hover",
    fontWeight: 700,
    borderRight: "2px solid",
    borderRightColor: "divider",
  };
  const generalCellSx = {
    backgroundColor: "action.hover",
    borderRight: "2px solid",
    borderRightColor: "divider",
  };

  const stickyGeneralHeaderSx = {
    position: "sticky",
    left: FIRST_COL_WIDTH,
    zIndex: 4,
    backgroundColor: "action.hover",
  };
  const stickyGeneralCellSx = {
    position: "sticky",
    left: FIRST_COL_WIDTH,
    zIndex: 1,
    backgroundColor: "action.hover",
  };

  const accountTypes = ["Debito", "Credito"];
  const popularBanks = [
    "AMEX",
    "BBVA",
    "Bajío",
    "Banamex",
    "Bancrea",
    "Banco Azteca",
    "Bancoppel",
    "Banjército",
    "Bankaool",
    "Banorte",
    "Bansi",
    "HSBC",
    "Inbursa",
    "Intercam Banco",
    "Monex",
    "Multiva",
    "Santander",
    "ScotiaBank",
    "Otro",
  ];

  const updateRecord = (id, field, value) => {
    setRecords((prev) => {
      const updated = prev.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]: value,
              ...(field === "cuenta" && { apodo: value }),
            }
          : r
      );
      if (field === "fecha") {
        return [...updated].sort(
          (a, b) => new Date(a.fecha) - new Date(b.fecha)
        );
      }
      return updated;
    });
  };

  const addRecord = async () => {
    const newRecord = {
      id: uid(),
      fecha: "",
      concepto: "",
      titularTarjeta: "",
      importe: "",
      cuenta: "",
      isEditable: true,
      conceptoCatalogo: "",
      agrupacion: "",
      estadoFinanciero: "",
      descripcion: "",
      apodo: "",
    };

    const updatedRecords = [newRecord, ...recordsRef.current];
    setRecords(updatedRecords);

    const { year, month } = selectedPeriodRef.current;
    await persistRecords(updatedRecords, year, month);
  };

  const deleteRecord = async (id) => {
    const updatedRecords = recordsRef.current.filter((r) => r.id !== id);
    setRecords(updatedRecords);
    const { year, month } = selectedPeriodRef.current;
    await persistRecords(updatedRecords, year, month);
  };

  const handleAddAccountOpen = () => setOpenAddAccount(true);

  const handleAddAccountClose = () => {
    setOpenAddAccount(false);
    setNewAccount({
      tipoCuenta: "",
      apodo: "",
      titular: "",
      banco: "",
      numeroCuenta: "",
      saldoBase: "",
    });
  };

  const handleAccountChange = (field, value) => {
    setNewAccount((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAccount = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl("api/v1/balanza/cuentas"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...newAccount, moneda: "mxn" }),
      });

      if (response.ok) {
        handleAddAccountClose();
        fetchAccounts();
      } else {
        setStatus("Error al agregar la cuenta");
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("Error de conexión al agregar cuenta");
    }
  };

  const handleDeleteAccount = async (cuenta) => {
    if (cuenta.tipoCuenta === "Efectivo") {
      setStatus("La cuenta Efectivo no puede eliminarse");
      return;
    }

    // Calcular saldo disponible igual que en la tabla (acumulado histórico)
    const agg = aggByAccount.get(cuenta.apodo);
    const entradasBrutas = agg ? agg.entradasBrutas : 0;
    const salidasBrutas = agg ? agg.salidasBrutas : 0;
    const esCredito = cuenta.tipoCuenta === "Credito";
    const entradas = esCredito ? -entradasBrutas : entradasBrutas;
    const salidas = esCredito ? salidasBrutas : -salidasBrutas;
    const diferencia = entradas + salidas;
    const resumenCuenta = (resumenCuentas || []).find(
      (c) => c.apodo === cuenta.apodo
    );
    const saldoInicial = toNumber(
      resumenCuenta?.saldoInicial ?? cuenta.saldoBase ?? 0
    );
    const saldoDisponible = saldoInicial + diferencia;

    if (saldoDisponible !== 0) {
      setStatus(
        `No se puede eliminar la cuenta porque su saldo disponible es $${formatCurrency(saldoDisponible)} (debe ser $0.00)`
      );
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        apiUrl(`api/v1/balanza/cuentas/${cuenta.apodo}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        setStatus("Cuenta eliminada con éxito");
        fetchAccounts();
      } else {
        setStatus("Error al eliminar la cuenta");
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("Error de conexión al eliminar cuenta");
    }
  };

  const handleUploadCSVOpen = async () => {
    setOpenUploadCSV(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(apiUrl("api/v1/balanza/cuentas"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(Array.isArray(data) ? data : []);
      } else {
        console.error("Error al cargar cuentas");
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleUploadCSVClose = () => {
    setOpenUploadCSV(false);
    setCsvFile(null);
    setSelectedAccount(null);
  };

  const handleUploadCSV = async () => {
    if (!csvFile || !selectedAccount) return;

    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");

      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append(
        "cuenta",
        JSON.stringify({
          tipoCuenta: selectedAccount.tipoCuenta,
          apodo: selectedAccount.apodo,
          titular: selectedAccount.titular,
          banco: selectedAccount.banco,
          numeroCuenta: selectedAccount.numeroCuenta,
        })
      );

      const { year, month } = selectedPeriodRef.current;

      const res = await fetch(
        apiUrl(
          `api/v1/balanza/entradas-salidas?year=${year}&month=${month}`
        ),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (res.ok) {
        const data = await res.json();
        const updatedRecords = await new Promise((resolve) => {
          setRecords((prev) => {
            const merged = [
              ...prev,
              ...data.map((r) => ({
                ...r,
                id: uid(),
                cuenta: r.cuenta || r.apodo || "",
                importe: toNumber(r.importe),
                isEditable: true,
                conceptoCatalogo: "",
                agrupacion: "",
                estadoFinanciero: "",
              })),
            ].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
            resolve(merged);
            return merged;
          });
        });

        await persistRecords(updatedRecords, year, month);
        setStatus("Archivo cargado con éxito");
        handleUploadCSVClose();
      } else {
        setStatus("Error al subir CSV");
      }
    } catch (err) {
      console.error(err);
      setStatus("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    setRecords([]);
    setOpenDeleteAll(false);
    const { year, month } = selectedPeriodRef.current;
    await persistRecords([], year, month);
  };

  const handleOpenDesglose = (record) => {
    setDesgloseRecord(record);
    setDesgloseLines([
      { importe: "", descripcion: "", conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "" },
    ]);
    setOpenDesglose(true);
  };

  const handleCloseDesglose = () => {
    setOpenDesglose(false);
    setDesgloseRecord(null);
    setDesgloseLines([
      { importe: "", descripcion: "", conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "" },
    ]);
  };

  const handleDesgloseLineChange = (index, field, value) => {
    setDesgloseLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  };

  const handleAddDesgloseLine = () => {
    setDesgloseLines((prev) => [
      ...prev,
      { importe: "", descripcion: "", conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "" },
    ]);
  };

  const handleRemoveDesgloseLine = (index) => {
    setDesgloseLines((prev) => prev.filter((_, i) => i !== index));
  };

  const desgloseSuma = desgloseLines.reduce(
    (sum, line) => sum + toNumber(line.importe),
    0
  );

  const desgloseMontoOriginal = toNumber(desgloseRecord?.importe);

  const desgloseIsValid =
    desgloseLines.length > 0 &&
    desgloseLines.every((line) => toNumber(line.importe) > 0) &&
    Math.abs(desgloseSuma - desgloseMontoOriginal) < 0.01;

  const handleConfirmDesglose = async () => {
    if (!desgloseIsValid || !desgloseRecord) return;

    // El desglose y su cantidad total se registran en la cuenta Efectivo
    const efectivo = accounts.find((a) => a.tipoCuenta === "Efectivo");
    const efectivoApodo =
      efectivo?.apodo || desgloseRecord.apodo || desgloseRecord.cuenta || "";

    // La salida del monto original se categoriza como "Capitalizaciones a
    // cuentas propias" con signo negativo.
    const salidaCategorizada = recordsRef.current.map((r) =>
      r.id === desgloseRecord.id ? { ...r, ...CAPITALIZACION_SALIDA } : r
    );

    const entradaNeutralizante = {
      id: uid(),
      fecha: desgloseRecord.fecha,
      concepto: "Entrada",
      titularTarjeta: desgloseRecord.titularTarjeta || "",
      importe: desgloseMontoOriginal,
      cuenta: efectivoApodo,
      isEditable: true,
      // Contraparte con signo positivo del monto original
      ...CAPITALIZACION_ENTRADA,
      descripcion: desgloseRecord.descripcion || "",
      apodo: efectivoApodo,
    };

    const nuevasSalidas = desgloseLines.map((line) => ({
      id: uid(),
      fecha: desgloseRecord.fecha,
      concepto: "Salida",
      titularTarjeta: desgloseRecord.titularTarjeta || "",
      importe: toNumber(line.importe),
      cuenta: efectivoApodo,
      isEditable: true,
      conceptoCatalogo: line.conceptoCatalogo || "",
      agrupacion: line.agrupacion || "",
      estadoFinanciero: line.estadoFinanciero || "",
      descripcion: line.descripcion || "",
      apodo: efectivoApodo,
    }));

    const updatedRecords = [
      ...salidaCategorizada,
      entradaNeutralizante,
      ...nuevasSalidas,
    ].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    setRecords(updatedRecords);

    const { year, month } = selectedPeriodRef.current;
    await persistRecords(updatedRecords, year, month);

    handleCloseDesglose();
  };

  const uniformSx = {
    "& .MuiOutlinedInput-root": {
      height: 40,
    },
    "& .MuiOutlinedInput-input, & .MuiSelect-select": {
      padding: "8px 12px",
      lineHeight: "24px",
      fontSize: "14px",
    },
  };

  const fieldProps = {
    size: "small",
    variant: "outlined",
    fullWidth: true,
    sx: uniformSx,
  };

  const readOnlyProps = {
    size: "small",
    variant: "outlined",
    fullWidth: true,
    sx: uniformSx,
    InputProps: { readOnly: true },
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h4" fontWeight="bold">
                  Entradas y Salidas
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Año</InputLabel>
                  <Select
                    value={selectedPeriod.year}
                    label="Año"
                    onChange={(e) =>
                      handlePeriodChange("year", e.target.value)
                    }
                  >
                    {Array.from(
                      { length: now.getFullYear() - 2022 },
                      (_, i) => 2023 + i
                    ).map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleExportExcel}
                  disabled={loadingExcel || loading}
                  startIcon={<Download />}
                >
                  {loadingExcel ? "Generando..." : "Generar Excel"}
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  onClick={() => navigate("/")}
                  disabled={loading}
                >
                  Regresar
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const { year, month } = selectedPeriodRef.current;
                    await persistRecords(recordsRef.current, year, month);
                    setStatus("Guardado con éxito");
                  }}
                  disabled={loading}
                  startIcon={<Save />}
                >
                  {loading ? "Guardando..." : "Guardar"}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {MONTHS.map((m, i) => {
                const isFuture =
                  selectedPeriod.year === now.getFullYear() && i + 1 > now.getMonth() + 1;
                return (
                  <Chip
                    key={m}
                    label={m.substring(0, 3)}
                    size="small"
                    disabled={isFuture}
                    variant={
                      selectedPeriod.month === i + 1 ? "filled" : "outlined"
                    }
                    color={
                      selectedPeriod.month === i + 1 ? "primary" : "default"
                    }
                    onClick={() => !isFuture && handlePeriodChange("month", i + 1)}
                    sx={{ fontWeight: selectedPeriod.month === i + 1 ? 700 : 400 }}
                  />
                );
              })}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {status && (
        <Alert
          severity={status.includes("éxito") ? "success" : "error"}
          sx={{ mb: 3 }}
        >
          {status}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardHeader
          title="Resumen de Cuentas"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleAddAccountOpen}
                disabled={loading}
                startIcon={<Add />}
                size="small"
              >
                Nueva Cuenta
              </Button>
            </Stack>
          }
        />
        <Divider />
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}></AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={stickyFirstColHeaderSx}></TableCell>
                    <TableCell
                      sx={{ ...generalHeaderSx, ...stickyGeneralHeaderSx }}
                    >
                      <strong>General</strong>
                    </TableCell>
                    {sortedAccounts.map((cuenta, i) => (
                      <TableCell key={cuenta.apodo} sx={accountColumnSx(i)}>
                        {cuenta.tipoCuenta !== "Efectivo" &&
                          cuenta.tipoCuenta !== "General" && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              {cuenta.tipoCuenta}
                            </Typography>
                          )}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography variant="subtitle2">
                            <strong>{cuenta.apodo}</strong>
                          </Typography>
                          {cuenta.tipoCuenta !== "Efectivo" && (
                            <Tooltip title="Eliminar cuenta">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAccount(cuenta)}
                                sx={{ ml: 1, color: "error.main" }}
                              >
                                <Delete fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    "Entradas",
                    "Salidas",
                    "Diferencia",
                    "Saldo Disponible",
                    "Saldo Inicial",
                  ].map((concepto) => (
                    <TableRow key={concepto}>
                      <TableCell sx={stickyFirstColCellSx}>
                        {concepto}
                      </TableCell>

                      <TableCell
                        sx={{ ...generalCellSx, ...stickyGeneralCellSx }}
                      >
                        {(() => {
                          const v =
                            concepto === "Entradas"
                              ? generalTotals.entradasTotal
                              : concepto === "Salidas"
                              ? generalTotals.salidasTotal
                              : concepto === "Diferencia"
                              ? generalTotals.diferenciaTotal
                              : concepto === "Saldo Disponible"
                              ? generalTotals.saldoDisponibleTotal
                              : generalTotals.saldoInicialTotal;
                          return <>${formatCurrency(v)}</>;
                        })()}
                      </TableCell>

                      {sortedAccounts.map((cuenta, i) => {
                        const agg = aggByAccount.get(cuenta.apodo);
                        const entradasBrutas = agg ? agg.entradasBrutas : 0;
                        const salidasBrutas = agg ? agg.salidasBrutas : 0;

                        const esCredito = cuenta.tipoCuenta === "Credito";

                        const entradas = esCredito
                          ? -entradasBrutas
                          : entradasBrutas;
                        const salidas = esCredito
                          ? salidasBrutas
                          : -salidasBrutas;
                        const diferencia = entradas + salidas;

                        const resumenCuenta = (resumenCuentas || []).find(
                          (c) => c.apodo === cuenta.apodo
                        );

                        const saldoInicial = toNumber(
                          resumenCuenta?.saldoInicial ?? cuenta.saldoBase ?? 0
                        );

                        const saldoDisponible = saldoInicial + diferencia;

                        const valor = {
                          Entradas: Math.abs(entradasBrutas),
                          Salidas: Math.abs(salidasBrutas),
                          Diferencia:
                            Math.abs(entradasBrutas) - Math.abs(salidasBrutas),
                          "Saldo Disponible": saldoDisponible,
                          "Saldo Inicial": saldoInicial,
                        }[concepto];

                        return (
                          <TableCell key={cuenta.apodo} sx={accountColumnSx(i)}>
                            ${formatCurrency(valor)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Card>

      <Card>
        <CardHeader
          title="Registros de Movimientos"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setOpenDeleteAll(true)}
                disabled={loading || records.length === 0}
                startIcon={<Delete />}
                size="small"
              >
                Borrar todo
              </Button>
              <Button
                variant="contained"
                onClick={handleUploadCSVOpen}
                disabled={loading}
                startIcon={<CloudUpload />}
                size="small"
              >
                Subir Estado de Cuenta
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={addRecord}
                disabled={loading}
                size="small"
              >
                Nuevo Registro
              </Button>
            </Stack>
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Importe</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Catálogo</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Agrupación</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Estado Financiero
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loadingRecords ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 6, border: 0 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                        <CircularProgress size={32} sx={{ color: "grey.400" }} />
                        <Typography variant="body2" sx={{ color: "grey.500" }}>
                          Cargando movimientos...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 5, border: 0 }}>
                      <Typography variant="body2" align="center" sx={{ color: "grey.400" }}>
                        No hay movimientos en este periodo
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : records.map((record) => (
                  <TableRow
                    key={record.id}
                    sx={{
                      backgroundColor:
                        record.concepto === "Entrada"
                          ? "rgba(0, 255, 0, 0.03)"
                          : record.concepto === "Salida"
                          ? "rgba(255, 0, 0, 0.02)"
                          : "inherit",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell>
                      {record.isEditable ? (
                        (() => {
                          const startOfMonth = new Date(
                            selectedPeriod.year,
                            selectedPeriod.month - 1,
                            1
                          );
                          const endOfMonth = new Date(
                            selectedPeriod.year,
                            selectedPeriod.month,
                            0
                          );

                          return (
                            <TextField
                              type="date"
                              value={
                                record.fecha
                                  ? record.fecha.substring(0, 10)
                                  : ""
                              }
                              onChange={(e) =>
                                updateRecord(record.id, "fecha", e.target.value)
                              }
                              inputProps={{
                                min: startOfMonth.toISOString().split("T")[0],
                                max: endOfMonth.toISOString().split("T")[0],
                              }}
                              {...fieldProps}
                            />
                          );
                        })()
                      ) : (
                        <TextField
                          value={record.fecha || ""}
                          {...readOnlyProps}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {record.isEditable ? (
                        <FormControl
                          {...fieldProps}
                          sx={{ ...uniformSx, minWidth: 140 }}
                        >
                          <Select
                            value={
                              accounts.some((acc) => acc.apodo === record.cuenta)
                                ? record.cuenta
                                : ""
                            }
                            onChange={(e) =>
                              updateRecord(record.id, "cuenta", e.target.value)
                            }
                            displayEmpty
                            input={<OutlinedInput />}
                          >
                            <MenuItem disabled value="">
                              {record.cuenta && !accounts.some((acc) => acc.apodo === record.cuenta)
                                ? `${record.cuenta} (eliminada)`
                                : "Selecciona cuenta"}
                            </MenuItem>
                            {accounts.map((acc) => (
                              <MenuItem key={acc.apodo} value={acc.apodo}>
                                {acc.apodo}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          value={record.cuenta || ""}
                          {...readOnlyProps}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {record.isEditable ? (
                        <FormControl
                          {...fieldProps}
                          sx={{ ...uniformSx, minWidth: 140 }}
                        >
                          <Select
                            value={record.concepto || ""}
                            onChange={(e) =>
                              updateRecord(
                                record.id,
                                "concepto",
                                e.target.value
                              )
                            }
                            displayEmpty
                            input={<OutlinedInput />}
                          >
                            <MenuItem disabled value="">
                              Selecciona
                            </MenuItem>
                            <MenuItem value="Entrada">Entrada</MenuItem>
                            <MenuItem value="Salida">Salida</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          value={record.concepto || ""}
                          {...readOnlyProps}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <ImporteCell
                        record={record}
                        updateRecord={updateRecord}
                        formatCurrency={formatCurrency}
                        fieldProps={fieldProps}
                      />
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={record.descripcion || ""}
                        arrow
                        placement="top"
                      >
                        {record.isEditable ? (
                          <TextField
                            value={record.descripcion || ""}
                            onChange={(e) =>
                              updateRecord(
                                record.id,
                                "descripcion",
                                e.target.value
                              )
                            }
                            onBlur={(e) => {
                              const finalValue = e.target.value;
                              updateRecord(record.id, "descripcion", finalValue);
                              const updatedRecords = recordsRef.current.map(
                                (r) =>
                                  r.id === record.id
                                    ? { ...r, descripcion: finalValue }
                                    : r
                              );
                              const { year, month } = selectedPeriodRef.current;
                              persistRecords(updatedRecords, year, month);
                            }}
                            {...fieldProps}
                          />
                        ) : (
                          <TextField
                            value={record.descripcion || ""}
                            {...readOnlyProps}
                          />
                        )}
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      {record.isEditable ? (
                        <FormControl {...fieldProps}>
                          <Select
                            value={record.conceptoCatalogo || ""}
                            onChange={(e) => {
                              const selected = catalogoConceptos.find(
                                (c) => c.conceptoCatalogo === e.target.value
                              );
                              updateRecord(
                                record.id,
                                "conceptoCatalogo",
                                e.target.value
                              );
                              updateRecord(
                                record.id,
                                "agrupacion",
                                selected ? selected.agrupacion : ""
                              );
                              updateRecord(
                                record.id,
                                "estadoFinanciero",
                                selected ? selected.estadoFinanciero : ""
                              );
                            }}
                            displayEmpty
                            input={<OutlinedInput />}
                          >
                            <MenuItem disabled value="">
                              Selecciona
                            </MenuItem>
                            {catalogoConceptos.map((c, i) => (
                              <MenuItem key={i} value={c.conceptoCatalogo}>
                                {c.conceptoCatalogo}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          value={record.conceptoCatalogo || ""}
                          {...readOnlyProps}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={record.agrupacion || ""}
                        {...readOnlyProps}
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={record.estadoFinanciero || ""}
                        {...readOnlyProps}
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title="Eliminar">
                          <IconButton
                            onClick={() => deleteRecord(record.id)}
                            disabled={loading}
                            size="small"
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {record.concepto === "Salida" && (
                          <Button
                            onClick={() => handleOpenDesglose(record)}
                            disabled={loading}
                            size="small"
                            variant="outlined"
                            startIcon={<CallSplit sx={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              minWidth: "auto",
                              py: 0.1,
                              px: 0.8,
                              fontSize: "0.7rem",
                              lineHeight: 1.4,
                              "& .MuiButton-startIcon": { mr: 0.4 },
                            }}
                          >
                            Desglosar
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={openAddAccount}
        onClose={handleAddAccountClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar Nueva Cuenta</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
              <InputLabel>Tipo de Cuenta</InputLabel>
              <Select
                value={newAccount.tipoCuenta}
                label="Tipo de Cuenta"
                onChange={(e) =>
                  handleAccountChange("tipoCuenta", e.target.value)
                }
                input={<OutlinedInput label="Tipo de Cuenta" />}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
              <InputLabel>Banco</InputLabel>
              <Select
                value={newAccount.banco}
                label="Banco"
                onChange={(e) => handleAccountChange("banco", e.target.value)}
                input={<OutlinedInput label="Banco" />}
              >
                {popularBanks.map((banco) => (
                  <MenuItem key={banco} value={banco}>
                    {banco}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Titular"
              value={newAccount.titular}
              onChange={(e) => handleAccountChange("titular", e.target.value)}
              fullWidth
              size="medium"
            />

            <TextField
              label="Apodo"
              value={newAccount.apodo}
              onChange={(e) => handleAccountChange("apodo", e.target.value)}
              fullWidth
              size="medium"
            />

            <TextField
              label="Número de Cuenta"
              value={newAccount.numeroCuenta}
              onChange={(e) =>
                handleAccountChange("numeroCuenta", e.target.value)
              }
              fullWidth
              size="medium"
            />

            <TextField
              label="Saldo Inicial"
              type="number"
              value={newAccount.saldoBase}
              onChange={(e) => handleAccountChange("saldoBase", e.target.value)}
              fullWidth
              size="medium"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddAccountClose}>Cancelar</Button>
          <Button
            onClick={handleSaveAccount}
            variant="contained"
            disabled={
              !newAccount.tipoCuenta ||
              !newAccount.apodo ||
              !newAccount.titular ||
              !newAccount.banco ||
              !newAccount.numeroCuenta ||
              newAccount.saldoBase === ""
            }
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUploadCSV}
        onClose={handleUploadCSVClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subir estado de cuenta — {MONTHS[selectedPeriod.month - 1]} {selectedPeriod.year}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Solo se agregarán los movimientos que correspondan a {MONTHS[selectedPeriod.month - 1]}. Si tu estado de cuenta incluye otros meses, esos movimientos se ignorarán.
          </Alert>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
              <InputLabel>Cuenta</InputLabel>
              <Select
                value={selectedAccount ? JSON.stringify(selectedAccount) : ""}
                onChange={(e) => {
                  const val = accounts.find(
                    (acc) => JSON.stringify(acc) === e.target.value
                  );
                  setSelectedAccount(val || null);
                }}
                label="Cuenta"
                input={<OutlinedInput label="Cuenta" />}
              >
                {accounts.filter((acc) => acc.tipoCuenta !== "Efectivo")
                  .length === 0 ? (
                  <MenuItem disabled>No hay cuentas disponibles</MenuItem>
                ) : (
                  accounts
                    .filter((acc) => acc.tipoCuenta !== "Efectivo")
                    .map((acc) => (
                      <MenuItem key={acc.apodo} value={JSON.stringify(acc)}>
                        {`${acc.tipoCuenta} - ${acc.banco} (${acc.apodo})`}
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
            >
              Seleccionar PDF
              <input
                type="file"
                accept=".pdf"
                hidden
                onChange={(e) => setCsvFile(e.target.files[0])}
              />
            </Button>

            {csvFile && (
              <Typography variant="body2">
                Archivo seleccionado: {csvFile.name}
              </Typography>
            )}

            {loading && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 2 }}>
                <CircularProgress size={32} sx={{ color: "grey.400" }} />
                <Typography variant="body2" sx={{ color: "grey.500" }}>
                  {csvFile?.name?.toLowerCase().endsWith(".pdf")
                    ? "Analizando PDF con IA... esto puede tardar unos segundos"
                    : "Procesando archivo..."}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCSVClose} disabled={loading}>Cancelar</Button>
          <Button
            onClick={handleUploadCSV}
            variant="contained"
            disabled={!csvFile || !selectedAccount || loading}
          >
            {loading ? "Procesando..." : "Subir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDesglose}
        onClose={handleCloseDesglose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Desglosar Salida</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {desgloseRecord?.descripcion && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  wordBreak: "break-word",
                  maxHeight: 80,
                  overflowY: "auto",
                }}
              >
                {desgloseRecord.descripcion}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1">
                <strong>Monto original:</strong> ${formatCurrency(desgloseMontoOriginal)}
              </Typography>
              <Typography
                variant="subtitle1"
                color={desgloseIsValid ? "success.main" : "error.main"}
              >
                <strong>Suma desglose:</strong> ${formatCurrency(desgloseSuma)}
              </Typography>
            </Box>

            <Divider />

            {desgloseLines.map((line, index) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 1, alignItems: "center" }}
              >
                <TextField
                  label="Monto"
                  type="text"
                  value={line.importe}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, "");
                    if ((raw.match(/\./g) || []).length > 1) return;
                    handleDesgloseLineChange(index, "importe", raw);
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, "");
                    handleDesgloseLineChange(
                      index,
                      "importe",
                      raw === "" ? "" : Number(raw)
                    );
                  }}
                  placeholder="$0.00"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Descripción"
                  value={line.descripcion}
                  onChange={(e) =>
                    handleDesgloseLineChange(index, "descripcion", e.target.value)
                  }
                  size="small"
                  sx={{ flex: 2 }}
                />
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>Catálogo</InputLabel>
                  <Select
                    label="Catálogo"
                    value={line.conceptoCatalogo || ""}
                    onChange={(e) => {
                      const selected = catalogoConceptos.find(
                        (c) => c.conceptoCatalogo === e.target.value
                      );
                      setDesgloseLines((prev) =>
                        prev.map((l, i) =>
                          i === index
                            ? {
                                ...l,
                                conceptoCatalogo: e.target.value,
                                agrupacion: selected ? selected.agrupacion : "",
                                estadoFinanciero: selected
                                  ? selected.estadoFinanciero
                                  : "",
                              }
                            : l
                        )
                      );
                    }}
                  >
                    {catalogoConceptos.map((c, i) => (
                      <MenuItem key={i} value={c.conceptoCatalogo}>
                        {c.conceptoCatalogo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => handleRemoveDesgloseLine(index)}
                  size="small"
                  color="error"
                  disabled={desgloseLines.length === 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddDesgloseLine}
              size="small"
            >
              Agregar línea
            </Button>

            {!desgloseIsValid && desgloseSuma > 0 && (
              <Alert severity="warning">
                La suma de los desgloses ($
                {formatCurrency(desgloseSuma)}) debe ser igual al monto
                original (${formatCurrency(desgloseMontoOriginal)}).
                Diferencia: ${formatCurrency(Math.abs(desgloseSuma - desgloseMontoOriginal))}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDesglose}>Cancelar</Button>
          <Button
            onClick={handleConfirmDesglose}
            variant="contained"
            disabled={!desgloseIsValid}
          >
            Confirmar desglose
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteAll}
        onClose={() => setOpenDeleteAll(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Borrar todos los registros</DialogTitle>
        <DialogContent>
          <Typography>
            Se eliminarán todos los registros de{" "}
            <strong>
              {MONTHS[selectedPeriod.month - 1]} {selectedPeriod.year}
            </strong>
            . Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteAll(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteAll}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default EntradasSalidas;